<template>
    <Teleport to="body">
        <AnimatePresence>
            <template v-if="modelValue">
                <Motion
                    :class="ui.backdrop()"
                    :variants="ModalBackdropVariants"
                    :initial="MotionPhase.Initial"
                    :animate="MotionPhase.Enter"
                    :exit="MotionPhase.Exit"
                    @mousedown.self="props.dismissible && close()" />
                <div :class="ui.wrapper()" :inert="!modelValue" tabindex="-1" role="dialog" aria-modal="true">
                    <Motion
                        :class="ui.content()"
                        :variants="ModalContentVariants"
                        :initial="MotionPhase.Initial"
                        :animate="MotionPhase.Enter"
                        :exit="MotionPhase.Exit">
                        <header :class="ui.header()">
                            <div :class="ui.headerContent()">
                                <slot name="header" />
                            </div>
                            <UButton
                                v-if="props.dismissible"
                                icon="br-icon:x"
                                :variant="ButtonVariants.Light"
                                :class="ui.closeButton()"
                                @click="close" />
                        </header>
                        <div :class="ui.body()">
                            <slot name="content" />
                        </div>
                        <footer :class="ui.footer()">
                            <slot name="footer" />
                        </footer>
                    </Motion>
                </div>
            </template>
        </AnimatePresence>
    </Teleport>
</template>

<script setup lang="ts">
    import type { Variant } from 'motion-v';

    import { MotionEase, MotionDuration, PointerEvents, Opacity } from '@constants/motion/defaults';
    import { MotionPhase, type MotionPhaseType } from '@constants/ui/motion-phase';
    import { ButtonVariants } from '@constants/button-variants';
    import { EXCLUSIVE_MODAL_OPENED_EVENT } from '@constants/common';

    const ModalBackdropVariants = Object.freeze({
        [MotionPhase.Initial]: {
            opacity: Opacity.Hidden,
            pointerEvents: PointerEvents.None
        },
        [MotionPhase.Enter]: {
            opacity: Opacity.Visible,
            pointerEvents: PointerEvents.Auto,
            transition: {
                duration: MotionDuration.Fade,
                ease: MotionEase.Out
            }
        },
        [MotionPhase.Exit]: {
            opacity: Opacity.Hidden,
            pointerEvents: PointerEvents.None,
            transition: {
                duration: MotionDuration.Fade,
                ease: MotionEase.In
            }
        }
    } satisfies Record<MotionPhaseType, Variant>);

    const ModalContentVariants = Object.freeze({
        [MotionPhase.Initial]: {
            opacity: Opacity.Hidden,
            pointerEvents: PointerEvents.None,
            transform: 'perspective(600px) translateZ(-80px) rotateX(4deg) rotateY(15deg) scale(0.88)'
        },
        [MotionPhase.Enter]: {
            opacity: Opacity.Visible,
            pointerEvents: PointerEvents.Auto,
            transform: 'perspective(600px) translateZ(0px) rotateX(0deg) rotateY(0deg) scale(1)',
            transition: {
                duration: MotionDuration.Modal,
                ease: MotionEase.Modal
            }
        },
        [MotionPhase.Exit]: {
            opacity: Opacity.Hidden,
            pointerEvents: PointerEvents.None,
            transform: 'perspective(600px) translateZ(-80px) rotateX(4deg) rotateY(15deg) scale(1)',
            transition: {
                duration: MotionDuration.Modal,
                ease: MotionEase.Modal
            }
        }
    } satisfies Record<MotionPhaseType, Variant>);

    defineOptions({ inheritAttrs: false });
    const props = defineProps({
        dismissible: {
            type: Boolean,
            default: true
        },
        exclusive: {
            type: Boolean
        }
    });

    const modelValue = defineModel<boolean>({ required: true });

    const ui = useComponentUi('baseModal');

    if (import.meta.client) {
        window.addEventListener(EXCLUSIVE_MODAL_OPENED_EVENT, closeForExclusiveModal);
    }

    onScopeDispose(() => {
        if (import.meta.client) {
            window.removeEventListener(EXCLUSIVE_MODAL_OPENED_EVENT, closeForExclusiveModal);
        }
    });

    function closeForExclusiveModal() {
        if (modelValue.value && !props.exclusive) {
            close();
        }
    }

    function close() {
        modelValue.value = false;
    }
</script>
